import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Tabs, Tab, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, getUserPups, checkAwards } from '../services/userService';
import { UserData } from '../types/auth';
import { Pup } from '../types/breed';
import PupCard from '../components/PupCard';

// Helper function to get award descriptions
const getAwardDescription = (awardName: string): string => {
  const descriptions: Record<string, string> = {
    "First Pup Post": "Posted your first pup to the community",
    "5 Pups Posted": "Shared 5 adorable pups with the community",
    "10 Pups Posted": "Shared 10 wonderful pups with the community",
    "25 Pups Posted": "Proud parent of 25 pups on PupNet",
    "50 Pups Posted": "Incredible collection of 50 pups shared",
    "100 Pups Posted": "True dog lover with 100 pups posted",
    "3 Different Breeds": "Posted dogs from 3 different breeds",
    "5 Different Breeds": "Posted dogs from 5 different breeds",
    "10 Different Breeds": "Posted dogs from 10 different breeds",
    "20 Different Breeds": "Posted dogs from 20 different breeds",
    "50 Different Breeds": "Posted dogs from 50 different breeds",
    "Rare Breed Spotter": "Found and shared a rare dog breed"
  };
  
  return descriptions[awardName] || "Achievement unlocked";
};

// Helper function to get the most popular reaction type from pups
const getMostPopularReaction = (pups: Pup[]): string => {
  // Count all reactions by type
  const reactionCounts: Record<string, number> = {};
  
  pups.forEach(pup => {
    if (pup.reactions) {
      Object.entries(pup.reactions).forEach(([type, count]) => {
        reactionCounts[type] = (reactionCounts[type] || 0) + count;
      });
    }
  });
  
  // Find the reaction type with the highest count
  let maxCount = 0;
  let mostPopular = 'None';
  
  Object.entries(reactionCounts).forEach(([type, count]) => {
    if (count > maxCount) {
      maxCount = count;
      
      // Map reaction type to emoji + label
      const emojiMap: Record<string, string> = {
        'cute': '😍 Cute',
        'want_it': '🦴 Good boy',
        'fluffy': '🧸 Fluffy',
        'sad': '🥺 Aww'
      };
      
      mostPopular = emojiMap[type] || type;
    }
  });
  
  return mostPopular;
};

const ProfilePage: React.FC = () => {
  const { user: currentUser, logout } = useAuth();
  const { username: urlUsername } = useParams<{ username?: string }>();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState<UserData | null>(null);
  const [pups, setPups] = useState<Pup[]>([]);
  const [awards, setAwards] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [newAwards, setNewAwards] = useState<string[]>([]);
  const [showAwardsAlert, setShowAwardsAlert] = useState<boolean>(false);

  // Determine which username to display
  const username = urlUsername || currentUser?.username;
  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!username) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user profile data
        const userData = await getUserProfile(username);
        setProfile(userData);
        
        // Fetch user's pups
        const pupsData = await getUserPups(username);
        setPups(pupsData);
        
        // Get user's awards if viewing own profile
        if (isOwnProfile) {
          const awardsData = await checkAwards();
          setAwards(awardsData.new_awards.length > 0 ? 
            awardsData.new_awards : 
            userData.awards || []
          );
          
          if (awardsData.new_awards.length > 0) {
            setNewAwards(awardsData.new_awards);
            setShowAwardsAlert(true);
          }
        } else if (userData.awards) {
          setAwards(userData.awards);
        }
      } catch (err) {
        setError('Failed to load profile data. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [username, isOwnProfile, currentUser]);

  if (loading) {
    return <div className="text-center p-5">Loading profile...</div>;
  }

  if (error || !profile) {
    return (
      <Alert variant="danger" className="m-3">
        {error || "Couldn't find this profile. Please check the username and try again."}
      </Alert>
    );
  }

  return (
    <div>
      {showAwardsAlert && newAwards.length > 0 && (
        <Alert 
          variant="success" 
          dismissible 
          onClose={() => setShowAwardsAlert(false)}
          className="mb-4"
        >
          <Alert.Heading>🎉 New Awards Earned!</Alert.Heading>
          <p>Congratulations! You've earned new awards:</p>
          <ul>
            {newAwards.map((award, index) => (
              <li key={index}>{award}</li>
            ))}
          </ul>
        </Alert>
      )}
      
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4} className="text-center">
              <div className="profile-avatar mb-3">
                <div 
                  className="avatar-placeholder rounded-circle mx-auto d-flex align-items-center justify-content-center bg-primary text-white" 
                  style={{ width: '150px', height: '150px', fontSize: '60px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                >
                  {profile.username.charAt(0).toUpperCase()}
                </div>
              </div>
              <h2 className="mb-1">{profile.username}</h2>
              {isOwnProfile && <p className="text-muted mb-3">{profile.email}</p>}
              
              <div className="d-flex justify-content-center mb-3">
                <div className="mx-2 text-center">
                  <h4>{profile.pup_count || pups.length}</h4>
                  <span className="text-muted">Pups</span>
                </div>
                <div className="mx-3 border-start border-end px-3 text-center">
                  <h4>{awards.length}</h4>
                  <span className="text-muted">Awards</span>
                </div>
                <div className="mx-2 text-center">
                  <h4>{pups.reduce((sum, pup) => sum + (Object.values(pup.reactions || {}).reduce((a, b) => a + b, 0)), 0)}</h4>
                  <span className="text-muted">Reactions</span>
                </div>
              </div>
              
              {isOwnProfile ? (
                <div>
                  <Link to="/upload">
                    <Button variant="primary" className="mt-2 px-4">
                      <i className="bi bi-plus-circle me-2"></i>
                      Upload New Pup
                    </Button>
                  </Link>
                  <div className="mt-3">
                    <Button 
                      variant="link" 
                      className="text-decoration-none logout-link" 
                      onClick={() => {
                        logout();
                        navigate('/');
                      }}
                    >
                      Logout
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="user-joined mt-2">
                  <small className="text-muted">
                    Joined {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString() : 'recently'}
                  </small>
                </div>
              )}
            </Col>
            
            <Col md={8}>
              <Tabs defaultActiveKey="pups" className="mb-3">
                <Tab eventKey="pups" title="Pups">
                  {pups.length === 0 ? (
                    <div className="text-center p-5">
                      <div className="mb-4">
                        <span className="display-1 text-muted">🐶</span>
                      </div>
                      <h4>No pups uploaded yet</h4>
                      {isOwnProfile ? (
                        <div className="mt-4">
                          <p className="text-muted mb-3">Share your dog photos with the community!</p>
                          <Link to="/upload">
                            <Button variant="primary">Upload Your First Pup</Button>
                          </Link>
                        </div>
                      ) : (
                        <p className="text-muted mt-3">
                          This user hasn't uploaded any pups yet.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Row>
                        {pups.map(pup => (
                          <Col key={pup.id} md={6} className="mb-4">
                            <PupCard pup={pup} />
                          </Col>
                        ))}
                      </Row>
                      
                      {pups.length > 0 && pups.length % 2 === 0 && isOwnProfile && (
                        <div className="text-center mt-3">
                          <Link to="/upload">
                            <Button variant="outline-primary">
                              <i className="bi bi-plus-circle me-2"></i> Upload More Pups
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </Tab>
                
                <Tab eventKey="awards" title="Awards">
                  {awards.length === 0 ? (
                    <div className="text-center p-5">
                      <div className="mb-4">
                        <span className="display-1 text-muted">🏆</span>
                      </div>
                      <h4>No awards earned yet</h4>
                      {isOwnProfile && (
                        <p className="text-muted mt-3">
                          Upload pups and interact with the community to earn awards!
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="awards-list p-2">
                      <Row>
                        {awards.map((award, index) => (
                          <Col key={index} md={6} className="mb-3">
                            <Card className="h-100 award-item border-0 shadow-sm">
                              <Card.Body>
                                <div className="d-flex align-items-center">
                                  <div className="award-icon-container bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                    <span className="award-icon fs-3" role="img" aria-label="Award Icon">🏆</span>
                                  </div>
                                  <div>
                                    <h5 className="mb-1">{award}</h5>
                                    <small className="text-muted">{getAwardDescription(award)}</small>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Tab>
                
                <Tab eventKey="stats" title="Stats">
                  <div className="p-3">
                    <h4 className="mb-4">Activity Summary</h4>
                    
                    <Row>
                      <Col md={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                          <Card.Body>
                            <div className="d-flex align-items-center mb-3">
                              <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-3">
                                <span className="fs-4" role="img" aria-label="Uploads">📸</span>
                              </div>
                              <h5 className="mb-0">Uploads</h5>
                            </div>
                            
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-2">
                                <span>Total Pups</span>
                                <strong>{pups.length}</strong>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span>Unique Breeds</span>
                                <strong>{new Set(pups.map(pup => pup.breed_id)).size}</strong>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Rare Breeds</span>
                                <strong>{pups.filter(pup => pup.is_rare).length}</strong>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col md={6} className="mb-4">
                        <Card className="h-100 border-0 shadow-sm">
                          <Card.Body>
                            <div className="d-flex align-items-center mb-3">
                              <div className="rounded-circle bg-danger bg-opacity-10 p-2 me-3">
                                <span className="fs-4" role="img" aria-label="Engagement">❤️</span>
                              </div>
                              <h5 className="mb-0">Engagement</h5>
                            </div>
                            
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-2">
                                <span>Total Reactions Received</span>
                                <strong>{pups.reduce((sum, pup) => sum + (Object.values(pup.reactions || {}).reduce((a, b) => a + b, 0)), 0)}</strong>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span>Most Popular Reaction</span>
                                <strong>{getMostPopularReaction(pups)}</strong>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Awards Earned</span>
                                <strong>{awards.length}</strong>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    
                    <div className="text-center text-muted mt-3">
                      <small>
                        Member since {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'recently'}
                      </small>
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProfilePage;